import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable, Column } from '@/components/ui/data-table';
import {
  benchmarkRatesService,
  BenchmarkRate,
  BenchmarkRateCreate,
} from '@/services/benchmark-rates.service';
import { dataImportService } from '@/services/data-import.service';
import { exportService } from '@/services/export.service';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Upload,
  Download,
  Edit,
  Save,
  X,
  TrendingUp,
  TrendingDown,
  Activity,
} from 'lucide-react';

interface BenchmarkRateDisplay extends BenchmarkRate {
  id?: number;
  term?: string;
}

const BenchmarkRatesPage: React.FC = () => {
  const [benchmarks, setBenchmarks] = useState<BenchmarkRateDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBenchmark, setEditingBenchmark] =
    useState<BenchmarkRateDisplay | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<BenchmarkRateCreate>({
    benchmark_date: new Date().toISOString().split('T')[0],
    benchmark_type: '',
    term_years: 0,
    rate: 0,
  });

  useEffect(() => {
    loadBenchmarks();
  }, []);

  const loadBenchmarks = async () => {
    try {
      setLoading(true);
      const data = await benchmarkRatesService.getCurrentBenchmarks();

      // Transform data to include ID and term for display
      const transformedData: BenchmarkRateDisplay[] = data.map((b, index) => ({
        ...b,
        id: index,
        term: b.tenor || 'N/A',
      }));

      setBenchmarks(transformedData);
    } catch (error) {
      console.error('Error loading benchmark rates:', error);
      toast.error('Failed to load benchmark rates');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBenchmark = async () => {
    try {
      await benchmarkRatesService.createBenchmark(formData);
      toast.success('Benchmark rate added successfully');
      setShowAddForm(false);
      setFormData({
        benchmark_date: new Date().toISOString().split('T')[0],
        benchmark_type: '',
        term_years: 0,
        rate: 0,
      });
      loadBenchmarks();
    } catch (error) {
      console.error('Error adding benchmark rate:', error);
      toast.error('Failed to add benchmark rate');
    }
  };

  const handleUpdateBenchmarks = async () => {
    try {
      const update = {
        effective_date: new Date().toISOString().split('T')[0],
        rates: benchmarks.map((b) => ({
          benchmark_type: b.benchmark_type,
          tenor: b.tenor,
          rate: b.rate,
        })),
        notes: 'Updated via Benchmark Rates Management Interface',
      };
      await benchmarkRatesService.updateBenchmarks(update);
      toast.success('Benchmark rates updated successfully');
      setEditingBenchmark(null);
    } catch (error) {
      console.error('Error updating benchmark rates:', error);
      toast.error('Failed to update benchmark rates');
    }
  };

  const handleEditBenchmark = (benchmark: BenchmarkRateDisplay) => {
    setEditingBenchmark(benchmark);
  };

  const handleSaveEdit = () => {
    if (editingBenchmark) {
      const updatedBenchmarks = benchmarks.map((b) =>
        b.id === editingBenchmark.id ? editingBenchmark : b
      );
      setBenchmarks(updatedBenchmarks);
      handleUpdateBenchmarks();
    }
  };

  const handleBulkUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          setIsImporting(true);
          const result = await dataImportService.importBenchmarkRates(file, {
            onProgress: (progress) => {
              // You could show progress here
              console.log(`Import progress: ${progress}%`);
            },
          });
          toast.success(
            `Successfully imported ${result.imported_count} benchmark rates`
          );
          loadBenchmarks(); // Reload the data
        } catch (error) {
          console.error('Error importing benchmark rates:', error);
          toast.error('Failed to import benchmark rates');
        } finally {
          setIsImporting(false);
        }
      }
    };
    input.click();
  };

  const handleExport = async () => {
    setIsExporting(true);
    const toastId = toast.loading('Exporting benchmark rates...', {
      duration: Infinity,
    });

    try {
      // exportBenchmarkRatesToExcel now handles download internally
      await exportService.exportBenchmarkRatesToExcel();
      toast.dismiss(toastId);
      toast.success('Export completed successfully!', { duration: 3000 });
    } catch (error) {
      console.error('Error exporting benchmark rates:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to export benchmark rates. Please try again.';
      toast.error(errorMessage, { id: toastId, duration: 5000 });
    } finally {
      setIsExporting(false);
    }
  };

  const formatRate = (rate: number) => {
    return `${(rate * 100).toFixed(3)}%`;
  };

  const getBenchmarkTypes = () => {
    // Fixed Rate Curves
    const fixedRateCurves = [
      'UST',
      'UK Govt',
      'UK_GOVT',
      'Euro Swaps',
      'EURO_SWAPS',
    ];
    
    // Floating Rate Forward Curves
    const floatingRateCurves = [
      'SOFR1M',
      'SOFR3M',
      'SOFR6M',
      'SONIA1M',
      'SONIA3M',
      'EURIBOR1M',
      'EURIBOR3M',
      'EURIBOR6M',
      'PRIME',
      'AUSBSW',
      'BBSW6M',
      'CADDOR',
      'CDOR3M',
      'SARON',
      'SORA',
      'TONAR',
      'GBPLIBOR1M',
      'GBPLIBOR3M',
      'CDI',
      'ESTR',
    ];
    
    return [...fixedRateCurves, ...floatingRateCurves];
  };

  const getTermYears = () => {
    return [0.25, 0.5, 1, 2, 3, 5, 7, 10, 20, 30];
  };

  const columns: Column<BenchmarkRateDisplay>[] = [
    {
      header: 'Benchmark Type',
      accessor: 'benchmark_type',
    },
    {
      header: 'Term',
      accessor: (row) => row.tenor || 'N/A',
    },
    {
      header: 'Rate',
      accessor: (row) => {
        if (editingBenchmark?.id === row.id) {
          return (
            <Input
              type="number"
              step="0.001"
              value={editingBenchmark?.rate || 0}
              onChange={(e) =>
                editingBenchmark &&
                setEditingBenchmark({
                  ...editingBenchmark,
                  rate: parseFloat(e.target.value) || 0,
                })
              }
              className="w-24"
              data-test-id="edit-rate-input"
            />
          );
        }
        return formatRate(row.rate);
      },
    },
    {
      header: 'Actions',
      accessor: (row) => {
        if (editingBenchmark?.id === row.id) {
          return (
            <div className="flex gap-2" data-test-id="edit-actions">
              <button
                onClick={handleSaveEdit}
                className="text-green-600 hover:text-green-900"
                data-test-id="save-edit-button"
              >
                <Save className="h-4 w-4" data-test-id="save-edit-icon" />
              </button>
              <button
                onClick={() => setEditingBenchmark(null)}
                className="text-red-600 hover:text-red-900"
                data-test-id="cancel-edit-button"
              >
                <X className="h-4 w-4" data-test-id="cancel-edit-icon" />
              </button>
            </div>
          );
        }
        return (
          <button
            onClick={() => handleEditBenchmark(row)}
            className="text-blue-600 hover:text-blue-900"
            data-test-id="edit-benchmark-button"
          >
            <Edit className="h-4 w-4" data-test-id="edit-benchmark-icon" />
          </button>
        );
      },
    },
  ];

  // Group benchmarks by type
  const groupedBenchmarks = benchmarks.reduce(
    (acc, benchmark) => {
      const type = benchmark.benchmark_type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(benchmark);
      return acc;
    },
    {} as Record<string, BenchmarkRateDisplay[]>
  );

  // Calculate statistics
  const calculateStats = () => {
    if (benchmarks.length === 0) return { min: 0, max: 0, avg: 0 };

    const rates = benchmarks.map((b) => b.rate * 100);
    const min = Math.min(...rates);
    const max = Math.max(...rates);
    const avg = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;

    return { min, max, avg };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div
        className="page-container"
        data-test-id="benchmark-rates-page-container"
      >
        <div className="page-inner" data-test-id="benchmark-rates-page-inner">
          <div
            className="flex items-center justify-center h-64"
            data-test-id="loading-wrapper"
          >
            <div
              className="text-lg text-gray-600"
              data-test-id="loading-benchmark-rates"
            >
              Loading benchmark rates...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="page-container"
      data-test-id="benchmark-rates-page-container"
    >
      <div className="page-inner" data-test-id="benchmark-rates-page-inner">
        {/* Header */}
        <div className="mb-8" data-test-id="header-section">
          <div
            className="flex justify-between items-center"
            data-test-id="header-wrapper"
          >
            <div data-test-id="header-content">
              <h1
                className="text-2xl font-bold mb-2"
                data-test-id="page-title"
              >
                Benchmark Rates Management
              </h1>
            </div>
            <div className="flex gap-2" data-test-id="header-actions">
              <Button
                variant="outline"
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center gap-2"
                data-test-id="add-rate-button"
              >
                <Plus className="h-4 w-4" data-test-id="add-rate-icon" />
                Add Rate
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={handleBulkUpload}
                disabled={isImporting}
                data-test-id="bulk-upload-button"
              >
                <Upload className="h-4 w-4" data-test-id="bulk-upload-icon" />
                {isImporting ? 'Importing...' : 'Bulk Upload'}
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={handleExport}
                disabled={isExporting}
                data-test-id="export-button"
              >
                <Download className="h-4 w-4" data-test-id="export-icon" />
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            </div>
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <Card className="p-6 mb-6" data-test-id="add-form-card">
            <h3
              className="text-lg font-semibold mb-4"
              data-test-id="add-form-title"
            >
              Add New Benchmark Rate
            </h3>
            <div
              className="grid grid-cols-1 md:grid-cols-4 gap-4"
              data-test-id="add-form-grid"
            >
              <div data-test-id="benchmark-date-field">
                <Label
                  htmlFor="benchmark_date"
                  data-test-id="benchmark-date-label"
                >
                  Benchmark Date
                </Label>
                <Input
                  id="benchmark_date"
                  type="date"
                  value={formData.benchmark_date}
                  onChange={(e) =>
                    setFormData({ ...formData, benchmark_date: e.target.value })
                  }
                  data-test-id="benchmark-date-input"
                />
              </div>
              <div data-test-id="benchmark-type-field">
                <Label
                  htmlFor="benchmark_type"
                  data-test-id="benchmark-type-label"
                >
                  Benchmark Type
                </Label>
                <select
                  id="benchmark_type"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.benchmark_type}
                  onChange={(e) =>
                    setFormData({ ...formData, benchmark_type: e.target.value })
                  }
                  data-test-id="benchmark-type-select"
                >
                  <option value="" data-test-id="benchmark-type-option-default">
                    Select...
                  </option>
                  {getBenchmarkTypes().map((type) => (
                    <option
                      key={type}
                      value={type}
                      data-test-id={`benchmark-type-option-${type.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div data-test-id="term-years-field">
                <Label htmlFor="term_years" data-test-id="term-years-label">
                  Term (Years)
                </Label>
                <select
                  id="term_years"
                  className="w-full px-3 py-2 border rounded-md"
                  value={formData.term_years}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      term_years: parseFloat(e.target.value),
                    })
                  }
                  data-test-id="term-years-select"
                >
                  <option value={0} data-test-id="term-years-option-default">
                    Select...
                  </option>
                  {getTermYears().map((term) => (
                    <option
                      key={term}
                      value={term}
                      data-test-id={`term-years-option-${term.toString().replace('.', '-')}`}
                    >
                      {term}
                    </option>
                  ))}
                </select>
              </div>
              <div data-test-id="rate-field">
                <Label htmlFor="rate" data-test-id="rate-label">
                  Rate (%)
                </Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.001"
                  value={formData.rate * 100}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      rate: parseFloat(e.target.value) / 100 || 0,
                    })
                  }
                  data-test-id="rate-input"
                />
              </div>
            </div>
            <div
              className="flex justify-end gap-2 mt-4"
              data-test-id="add-form-actions"
            >
              <Button
                variant="outline"
                onClick={() => setShowAddForm(false)}
                data-test-id="cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddBenchmark}
                data-test-id="add-rate-submit-button"
              >
                Add Rate
              </Button>
            </div>
          </Card>
        )}

        {/* Filter */}
        <div className="mb-6" data-test-id="filter-section">
          <Label htmlFor="type-filter" data-test-id="filter-label">
            Filter by Type
          </Label>
          <select
            id="type-filter"
            className="w-full md:w-64 px-3 py-2 border rounded-md"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            data-test-id="type-filter-select"
          >
            <option value="" data-test-id="filter-option-all">
              All Types
            </option>
            {getBenchmarkTypes().map((type) => (
              <option
                key={type}
                value={type}
                data-test-id={`filter-option-${type.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Rates Table */}
        <Card className="p-6" data-test-id="rates-table-card">
          {Object.entries(groupedBenchmarks)
            .filter(([type]) => !selectedType || type === selectedType)
            .map(([type, typeBenchmarks]) => (
              <div
                key={type}
                className="mb-8"
                data-test-id={`benchmark-group-${type.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <h3
                  className="text-lg font-semibold mb-4 text-gray-900"
                  data-test-id={`benchmark-group-title-${type.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {type} ({typeBenchmarks.length} rates)
                </h3>
                <DataTable
                  columns={columns}
                  data={typeBenchmarks}
                  keyExtractor={(row) =>
                    `${row.benchmark_type}-${row.tenor}-${row.id}`
                  }
                  emptyMessage="No benchmark rates found"
                  data-test-id={`benchmark-table-${type.toLowerCase().replace(/\s+/g, '-')}`}
                />
              </div>
            ))}
          {benchmarks.length === 0 && (
            <div
              className="text-center py-8 text-gray-500"
              data-test-id="empty-state"
            >
              No benchmark rates found. Click "Add Rate" to create one.
            </div>
          )}
        </Card>

        {/* Summary Statistics */}
        <div
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8"
          data-test-id="summary-statistics"
        >
          <Card className="p-6" data-test-id="rate-range-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="rate-range-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="rate-range-title"
              >
                Rate Range
              </h3>
              <Activity
                className="h-5 w-5 text-blue-600"
                data-test-id="rate-range-icon"
              />
            </div>
            <p className="text-sm text-gray-600" data-test-id="min-rate-value">
              Min: {stats.min.toFixed(3)}%
            </p>
            <p className="text-sm text-gray-600" data-test-id="max-rate-value">
              Max: {stats.max.toFixed(3)}%
            </p>
            <p className="text-sm text-gray-600" data-test-id="avg-rate-value">
              Avg: {stats.avg.toFixed(3)}%
            </p>
          </Card>
          <Card className="p-6" data-test-id="benchmark-types-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="benchmark-types-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="benchmark-types-title"
              >
                Benchmark Types
              </h3>
              <TrendingUp
                className="h-5 w-5 text-green-600"
                data-test-id="benchmark-types-icon"
              />
            </div>
            <p
              className="text-2xl font-bold"
              data-test-id="benchmark-types-count"
            >
              {Object.keys(groupedBenchmarks).length}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="benchmark-types-label"
            >
              Active types
            </p>
          </Card>
          <Card className="p-6" data-test-id="total-rates-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="total-rates-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="total-rates-title"
              >
                Total Rates
              </h3>
              <TrendingDown
                className="h-5 w-5 text-yellow-600"
                data-test-id="total-rates-icon"
              />
            </div>
            <p className="text-2xl font-bold" data-test-id="total-rates-count">
              {benchmarks.length}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="total-rates-label"
            >
              Configured rates
            </p>
          </Card>
          <Card className="p-6" data-test-id="last-updated-card">
            <div
              className="flex items-center justify-between mb-2"
              data-test-id="last-updated-header"
            >
              <h3
                className="text-lg font-semibold"
                data-test-id="last-updated-title"
              >
                Last Updated
              </h3>
              <Activity
                className="h-5 w-5 text-purple-600"
                data-test-id="last-updated-icon"
              />
            </div>
            <p className="text-sm font-medium" data-test-id="last-updated-date">
              {new Date().toLocaleDateString()}
            </p>
            <p
              className="text-sm text-gray-600"
              data-test-id="last-updated-label"
            >
              As of today
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BenchmarkRatesPage;
